import React, { useEffect, useMemo, useState } from "react";
import { useSocket } from "../../contexts/SocketContext";
import { useWebRTC, WebRTCProvider } from "../../contexts/WebRTCContext";
import { Panel } from "../../components/panels/Panel";
import { CamerasGrid } from "./components/CamerasGrid";
import { ActionButton } from "../../components/buttons/ActionButton";
import {
  ArrowPathIcon, CameraIcon,
  ClockIcon,
  PlayIcon,
  VideoCameraIcon,
  VideoCameraSlashIcon, XMarkIcon
} from "@heroicons/react/20/solid";
import { CircleStepProgressbar } from "../../components/progressbar/CircleStepProgressbar";
import { CamerasLayoutShortcuts } from "./components/CamerasLayoutShortcuts";
import {
  calendarDateTimetoDate,
  formatDateTimeFromTimestamp, formatSecondsToFullDateTime,
  formatSecondsToMinutesSeconds
} from "../../helpers/date/date-helper";
import { ConfirmModal } from "../../components/modals/ConfirmModal";
import { DateRangePicker } from "@heroui/date-picker";
import { useAlert } from "../../contexts/AlertContext";
import { postData } from "../../services/api/api-tools";
import Badge from "../../components/badges/Badge";
import { Tooltip } from "@heroui/react";
import { Loader } from "../../components/loading/Loader";

export const RecorderPage = ({ externalStreaming=false }) => {
  return (
    <WebRTCProvider>
      <RecorderLayout externalStreaming={externalStreaming}/>
    </WebRTCProvider>
  );
};

export const RecorderLayout = ({ externalStreaming }) => {
  const { socket, subscribeToEvent, unsubscribeFromEvent, sendEvent } = useSocket();
  const { cams, isConnecting, isError, detectCameras, steps, scheduledJob, setScheduledJob, clearScheduledJobLocal, currentJob } = useWebRTC();
  const { showAlert } = useAlert();

  const [manualRecordingStart, setManualRecordingStart] = useState(null);
  const [recordingSeconds, setRecordingSeconds] = useState(null);
  const [isConfirmStopOpen, setIsConfirmStopOpen] = useState(false);

  const activeRecordingStart = manualRecordingStart ?? (currentJob ? currentJob.start : null);
  const hasScheduledJob = scheduledJob !== null;

  // Calendar management
  const now = new Date();
  const nextHour = new Date(now); nextHour.setHours(nextHour.getHours() + 1);
  const defaultRange = {start: now, end: nextHour}
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [draftRange, setDraftRange] = useState({
    start: formatDateTimeFromTimestamp(defaultRange.start),
    end: formatDateTimeFromTimestamp(defaultRange.end),
  });
  const isDraftInPast = (() => {
    if (!draftRange?.start) return true;
    const startTs = calendarDateTimetoDate(draftRange.start).getTime();
    return startTs <= Date.now();
  })();

  const emitRecorderKit = (command, recordId=null) => {
    if (!socket) return;

    sendEvent("current_data", {
      command,
      width: 1920,
      height: 1080,
      record_id: recordId,
    });

    console.log('Recorder kit data sent:', command);
  };

  const handleCancelScheduledRecording = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_ADDRESS}streaming/clear_schedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        showAlert('error', 'Impossible d\'annuler l\'enregistrement programmé.');
        return;
      }

      clearScheduledJobLocal();
      showAlert('success', 'L\'enregistrement programmé a été annulé.');
    } catch {
      showAlert('error', 'Impossible d\'annuler l\'enregistrement programmé.');
    }
  };

  useEffect(() => {
    if (!isError) return;
    sendEvent('current_data', {command: 'stop'});
  }, [isError]);

  useEffect(() => {
    if (activeRecordingStart === null) {
      setRecordingSeconds(null);
      return;
    }

    const update = () => setRecordingSeconds(Math.floor((Date.now() / 1000) - activeRecordingStart));
    update();
    const intervalId = setInterval(update, 1000);

    return () => clearInterval(intervalId);
  }, [activeRecordingStart]);

  const handleStartRecording = async () => {
    const now = Math.floor((new Date()).getTime() / 1000);

    // Start UI immediately (no artificial scheduled job)
    setManualRecordingStart(now);

    emitRecorderKit('start');
  };

  const handleScheduleRecording = async () => {
    const start = Math.floor(calendarDateTimetoDate(draftRange.start).getTime() / 1000);
    const end = Math.floor(calendarDateTimetoDate(draftRange.end).getTime() / 1000);

    const res = await fetch(`${process.env.REACT_APP_ADDRESS}streaming/schedule/${start}/${end}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    if (!res.ok) {
      showAlert('error', 'Impossible de programmer l\'enregistrement.');
      return;
    }

    setScheduledJob({ start, end });

    showAlert('success', 'L\'enregistrement a été programmé. Veillez à ne pas arrêter le système.');
  };

  useEffect(() => {
    if (!socket) return;

    const handleReceivedData = (data) => {
      console.log(data);
      if (data?.start_stream) {
        detectCameras(false);
      }
    };

    subscribeToEvent('current_data', handleReceivedData);

    return () => {
      unsubscribeFromEvent('current_data', handleReceivedData);
    };
  }, [socket]);

  const processIsFinished = useMemo(() => steps.every((step) => step.status === 'complete'), [steps]);
  // const processIsFinished = true;

  return (
    <div className="h-full flex">
      <ConfirmModal
        onConfirm={() => {
          // Immediate UI reset
          setManualRecordingStart(null);

          // Stop recording via socket (enough to stop Python recording)
          emitRecorderKit('stop');

          // Only clear schedule if a scheduled recording was actually running
          if (currentJob !== null) {
            clearScheduledJobLocal();

            fetch(`${process.env.REACT_APP_ADDRESS}streaming/clear_schedule`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
            }).catch(() => {});
          }
        }}
        isOpen={isConfirmStopOpen}
        setIsOpen={setIsConfirmStopOpen}
        title="Terminer l'enregistrement"
        text="Êtes-vous sûr de vouloir terminer l'enregistrement ?"
      />

      {/*<Panel title="RACCOURCIS" width="w-1/6"/>*/}
      <Panel title="VUE LIVE" category="main" className="flex flex-col items-center relative" noPadding={true}>
        {
          externalStreaming && steps?.[0]?.status !== 'upcoming' && !processIsFinished &&
            <Loader/>
        }
        {
          isError ?
            <div className="h-full flex-1 flex flex-col items-center justify-center gap-16 z-10">
              <div className="w-2/3">
                <p className="text-center text-3xl">
                  Une erreur est survenue. Veuillez recommencer la procédure.
                </p>
              </div>

              <ActionButton text="Redémarrer" Icon={ArrowPathIcon} onClick={() => window.location.reload()} size="lg"/>
            </div>
          :
          <>
            <div className="relative h-full">
              {/* Instructions zone */}
              {
                externalStreaming && !processIsFinished ?
                <div className="h-full flex-1 flex flex-col items-center justify-center z-10 gap-8">
                  <CameraIcon className="text-perception-gray-700 size-32"/>
                  <p className="text-center text-3xl">
                    Pour commencer, démarrez la captation sur le téléphone.
                  </p>
                </div>
                :
                <>
                  {
                    !processIsFinished &&
                      <div className="h-full flex-1 flex flex-col items-center justify-center gap-16 z-10 px-8">
                        <div className="w-full lg:w-2/3">
                          <p className="text-center text-3xl">
                            Pour commencer, connectez toutes les caméras.<br/>
                          </p>
                          <p className="mt-8 text-center text-3xl">
                            Cliquez ensuite sur le bouton ci-dessous pour démarrer la captation.
                          </p>
                        </div>

                        <ActionButton text="Initialiser les caméras" Icon={PlayIcon} onClick={detectCameras} disabled={isConnecting} size="lg"/>
                        <CircleStepProgressbar steps={steps}/>
                      </div>
                  }
                </>
              }

              {/* Cameras space */}
              <div className={`flex-1 absolute inset-0 ${processIsFinished ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition`}>
                <CamerasGrid />
              </div>
            </div>

            {/* Action bar */}
            <div className={`p-4 pt-0 grid grid-cols-[1fr_4fr] md:grid-cols-[1fr_3fr_1fr] gap-y-2 items-center ${processIsFinished ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition`}>
              <div className="h-11 min-w-0">
                {
                  cams.length === 3 && <CamerasLayoutShortcuts/>
                }
              </div>

              <div className="flex justify-center gap-4 items-center px-4">
              {
                recordingSeconds !== null ?
                  <>
                    <div className="flex items-center gap-2">
                      <span className='relative flex h-3 w-3'>
                        <span className='animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-error'/>
                        <span className='relative inline-flex rounded-full h-3 w-3 bg-error' />
                      </span>
                      Enregistrement en cours ({formatSecondsToMinutesSeconds(recordingSeconds)})
                    </div>
                    <ActionButton text="Arrêter l'enregistrement" color="secondary" Icon={VideoCameraSlashIcon} onClick={() => setIsConfirmStopOpen(true)} disabled={!socket} />
                  </>
                :
                  <>
                    <Tooltip content="Un enregistrement est déjà programmé." showArrow={true} isDisabled={!hasScheduledJob}>
                      <ActionButton text="Démarrer l'enregistrement" Icon={VideoCameraIcon} onClick={handleStartRecording} disabled={!socket || hasScheduledJob} />
                    </Tooltip>

                    <div className="relative">
                      <Tooltip content="Un enregistrement est déjà programmé." showArrow={true} isDisabled={!hasScheduledJob}>
                        <ActionButton text="Programmer" Icon={ClockIcon} onClick={() => setIsCalendarOpen(!isCalendarOpen)} disabled={hasScheduledJob} />
                      </Tooltip>

                      <DateRangePicker
                        isOpen={isCalendarOpen}
                        onOpenChange={setIsCalendarOpen}
                        value={{ start: draftRange.start, end: draftRange.end }}
                        onChange={({ start, end }) => setDraftRange({ start, end })}
                        CalendarBottomContent={
                          <div className="flex justify-end gap-2 p-4 pt-0">
                            <ActionButton text="Annuler" color="cancel" onClick={() => setIsCalendarOpen(false)} size="sm"/>
                            <ActionButton
                              text="Programmer" size="sm" disabled={!draftRange?.start || !draftRange?.end || isDraftInPast}
                              onClick={() => {
                                handleScheduleRecording();
                                setIsCalendarOpen(false);
                              }}
                            />
                          </div>
                        }
                        classNames={{
                          base: "p-0",
                          inputWrapper: "p-0 min-h-0 h-auto bg-transparent",
                          selectorButton: "hidden",
                          innerWrapper: "absolute w-0 h-0 overflow-hidden opacity-0 pointer-events-none",
                          segment: "hidden",
                          separator: "hidden",
                          input: "w-0 h-0 p-0 m-0 opacity-0",
                          label: "hidden",
                        }}
                        className="absolute left-0 top-0 w-0 h-0 overflow-hidden p-0 m-0"
                        radius="none"
                        granularity="minute"
                        label={null}
                      />
                    </div>
                  </>
              }
              </div>

              {
                scheduledJob && !(recordingSeconds > 0) &&
                  <div className="text-left md:text-right text-sm col-span-2 md:col-span-1 flex md:flex-col gap-1 justify-center items-center md:items-end">
                    Enregistrement programmé
                    <div className="flex gap-2">
                      <Badge text={formatSecondsToFullDateTime(scheduledJob.start)} type="green"/>
                      <Tooltip content="Annuler l'enregistrement" showArrow={true} placement="top-end">
                        <ActionButton color="secondary" size="sm" Icon={XMarkIcon} onClick={handleCancelScheduledRecording}/>
                      </Tooltip>
                    </div>
                  </div>
              }
            </div>
          </>
        }
      </Panel>
    </div>
  );
};
